import CustomLoadingButton from 'Components/CustomLoadingButton'
import InfoTooltip from 'Components/InfoTooltip'
import ProductTariffFieldError from 'Components/ProductTariffFieldErrors'
import StyledLabel from 'Components/StyledLabel'
import TranslationInput from 'Components/TranslationInput'
import { WithLoader } from 'Components/WithLoader'
import {
  useGetAllowedTokensParameterGroupQuery,
  useGetCustomerGroupParameterGroupQuery,
  useGetProductGroupParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { AllowedActivationMethodsInfoTooltip } from 'Services/ProductManagement/Features/Product/Components/AllowedActivationMethodsInfoTooltip'
import MaximumDeviceTransfersTooltip from 'Services/ProductManagement/Features/Product/Components/MaximumDeviceTransfersTooltip'
import MaximumTripsPerDayInfoTooltip from 'Services/ProductManagement/Features/Product/Components/MaximumTripsPerDayInfoTooltip'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import {
  AllowedActivationMethods,
  createSingleProduct,
  DurationUnit,
  type Product,
  ProductUsages,
  updateSingleProduct
} from 'Services/ProductManagement/Features/Product/productSlice'
import type { VATDefinition } from 'Services/ProductManagement/Features/VATDefinition/types'
import { type LoadingStates, type Translation, useAppDispatch } from 'Store'
import type { ProductTariffError, ProductTariffViolation } from 'Util/ApiClient'
import { DEFAULT_VAT_DEFINITIONS, IS_WALTTI_SERVICE_ENVIRONMENT } from 'Util/constants'
import { calculateISODurationLength, createDurationMap } from 'Util/formatters'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { c, t } from 'ttag'

type FieldErrors = {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  activationValidDays: [],
  productCode: [],
  productGroup: [],
  duration: [],
  usageValidityDuration: [],
  usageTriggerEvent: [],
  maxPurchaseAdvanceDuration: [],
  customerGroup: [],
  allowedTokens: [],
  vatCode: [],
  translations: [],
  sharedUsage: [],
  strongIdentificationRequired: [],
  printZeroPrice: [],
  maxTripsPerDay: [],
  maxDeviceTransfers: []
}

interface ProductFormProps {
  product?: Product
  successBaseUrl: string
  submitLoading: LoadingStates
  vatDefinition?: VATDefinition | null
}

export default function ProductForm({
  product,
  successBaseUrl,
  submitLoading,
  vatDefinition
}: Readonly<ProductFormProps>) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isEdit = !!product

  const {
    data: productGroupParameterGroup = null,
    isLoading: isProductGroupParameterGroupLoading
  } = useGetProductGroupParameterGroupQuery(undefined, {
    refetchOnMountOrArgChange: true
  })

  const {
    data: customerGroupParameterGroup = null,
    isLoading: isCustomerGroupParameterGroupLoading
  } = useGetCustomerGroupParameterGroupQuery(undefined, {
    refetchOnMountOrArgChange: true
  })

  const {
    data: allowedTokensParameterGroup = null,
    isLoading: isAllowedTokensParameterGroupLoading
  } = useGetAllowedTokensParameterGroupQuery(undefined, {
    refetchOnMountOrArgChange: true
  })

  const [productDetails, setProductDetails] = useState<Partial<Product>>(
    (isEdit && product) || {
      activation_window: { duration: null, period: [] }
    }
  )
  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)
  const [validityFormat, setValidityFormat] = useState(DurationUnit.DAYS)
  const [validityLength, setValidityLength] = useState<number>()
  const [maxPurchaseAdvanceDurationFormat, setMaxPurchaseAdvanceDurationFormat] = useState(
    DurationUnit.DAYS
  )
  const [maxPurchaseAdvanceDuration, setMaxPurchaseAdvanceDuration] = useState<number>()
  const [activityWindowDuration, setActivityWindowDuration] = useState<number>()
  const [activityWindowDurationFormat, setActivityWindowDurationFormat] = useState(
    DurationUnit.DAYS
  )

  const handleProductISODurations = (product: Product) => {
    if (product.usage_validity_duration) {
      const { length, unit } = calculateISODurationLength(product.usage_validity_duration)
      setValidityLength(length)
      setValidityFormat(unit)
    }

    if (product.max_purchase_advance_duration) {
      const { length, unit } = calculateISODurationLength(product.max_purchase_advance_duration)
      setMaxPurchaseAdvanceDuration(length)
      setMaxPurchaseAdvanceDurationFormat(unit)
    }

    if (product.activation_window?.duration) {
      const { length, unit } = calculateISODurationLength(product.activation_window.duration)
      setActivityWindowDuration(length)
      setActivityWindowDurationFormat(unit)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress handleProductISODurations
  useEffect(() => {
    if (isEdit && product) {
      setProductDetails(product)
      handleProductISODurations(product)
    }
  }, [product])

  const handleChange = (
    value: string | string[] | Translation[] | number | boolean | undefined | null,
    field: keyof Product
  ) => {
    if (value !== undefined) {
      setProductDetails(prevState => ({ ...prevState, [field]: value }) as Product)
    }
  }

  const handleAllowedActivationMethodChange = (value: string | string[], field: keyof Product) => {
    setProductDetails(
      prevState =>
        ({
          ...prevState,
          [field]: value,
          // Nullify the max_purchase_advance_duration if the activation method is not timed.
          // If we don't nullify it manually, the value will remain even though the field is hidden
          max_purchase_advance_duration: !value.includes(AllowedActivationMethods.TIMED)
            ? null
            : prevState.max_purchase_advance_duration,
          activation_window: {
            ...prevState.activation_window,
            duration: !value.includes(AllowedActivationMethods.FIRST_USE)
              ? null
              : prevState.activation_window?.duration
          }
        }) as Product
    )
  }

  const handleSetTranslations = (translations: Translation[]) => {
    handleChange(translations, 'translations')
  }

  const handleValidityFormat = (value: DurationUnit) => {
    setValidityFormat(value)

    if (validityLength) {
      const durationMap = createDurationMap(validityLength)
      handleChange(durationMap[value], 'usage_validity_duration')
    }
  }

  const handleValidityLength = (value: number) => {
    setValidityLength(value)

    const durationMap = createDurationMap(value)
    handleChange(durationMap[validityFormat], 'usage_validity_duration')
  }

  const handleMaxPurchaseAdvanceDuration = (value: number) => {
    setMaxPurchaseAdvanceDuration(value)

    const durationMap = createDurationMap(value)
    handleChange(durationMap[maxPurchaseAdvanceDurationFormat], 'max_purchase_advance_duration')
  }

  const handleMaxPurchaseAdvanceDurationFormat = (value: DurationUnit) => {
    setMaxPurchaseAdvanceDurationFormat(value)

    if (maxPurchaseAdvanceDuration) {
      const durationMap = createDurationMap(maxPurchaseAdvanceDuration)
      handleChange(durationMap[value], 'max_purchase_advance_duration')
    }
  }

  const handleActivationWindowDuration = (value: number) => {
    setActivityWindowDuration(value)

    const durationMap = createDurationMap(value)
    setProductDetails(
      prevState =>
        ({
          ...prevState,
          activation_window: {
            ...prevState.activation_window,
            duration: durationMap[activityWindowDurationFormat]
          }
        }) as Product
    )
  }

  const handleActivationWindowDurationFormat = (value: DurationUnit) => {
    setActivityWindowDurationFormat(value)

    if (activityWindowDuration) {
      const durationMap = createDurationMap(activityWindowDuration)
      setProductDetails(
        prevState =>
          ({
            ...prevState,
            activation_window: {
              ...prevState.activation_window,
              duration: durationMap[value]
            }
          }) as Product
      )
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const productData = {
      ...productDetails
    }

    if (product) {
      dispatch(updateSingleProduct({ ...productData, id: product.id }))
        .then(unwrapResult)
        .then(() => {
          SnackbarUtils.success(t`Product successfully updated.`)
          navigate(successBaseUrl)
        })
        .catch(({ violations }: ProductTariffError) => {
          if (violations && violations.length > 0) {
            setValidationErrors(violations)
          }
          SnackbarUtils.error(t`There was an error updating the product.`)
        })
      return
    }
    dispatch(createSingleProduct(productData))
      .then(unwrapResult)
      .then((productResponse: Product) => {
        SnackbarUtils.success(t`Product successfully created.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_PRODUCT_PARAMETER_KEY]: productResponse.id
          })
        )
      })
      .catch(({ violations }: ProductTariffError) => {
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        }
        SnackbarUtils.error(t`There was an error creating the product.`)
      })
  }

  function setValidationErrors(violations: ProductTariffViolation[]) {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[violation.field] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  const productGroupOptions =
    productGroupParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  const customerGroupOptions =
    customerGroupParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []
  // Add empty option for customer group
  customerGroupOptions.unshift({
    label: '-',
    value: ''
  })

  const allowedTokenOptions =
    allowedTokensParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  const vatDefinitionOptions =
    vatDefinition?.vat_percentages?.map(({ vat_code, vat_percent }) => ({
      label: `${vat_code} (${vat_percent} %)`,
      value: vat_code
    })) ?? DEFAULT_VAT_DEFINITIONS

  const allowedActivationMethodOptions = [
    {
      label: t`Purchase`,
      value: AllowedActivationMethods.PURCHASE
    },
    {
      label: t`First use`,
      value: AllowedActivationMethods.FIRST_USE
    },
    {
      label: t`Timed`,
      value: AllowedActivationMethods.TIMED
    }
  ]

  const sharedUsageOptions = [
    {
      label: t`Single user`,
      value: ProductUsages.SINGLE_USER
    },
    {
      label: t`Concurrent users`,
      value: ProductUsages.CONCURRENT_USERS
    }
  ]

  const isSubmitLoading = submitLoading === 'pending'
  const isLoading =
    isProductGroupParameterGroupLoading ||
    isCustomerGroupParameterGroupLoading ||
    isAllowedTokensParameterGroupLoading

  return (
    <WithLoader loading={isLoading}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Box component="form" onSubmit={handleSubmit}>
            {/* PRODUCT CODE AND GROUP BOF */}
            <Box sx={{ display: 'flex' }}>
              <FormControl sx={{ minWidth: 240, mr: 3 }} size="small" required>
                <TextField
                  name="product_code"
                  required
                  label={t`Product code`}
                  size="small"
                  value={productDetails?.product_code}
                  error={fieldErrors.productCode.length > 0}
                  onChange={e => handleChange(e.target.value, 'product_code')}
                />
                <ProductTariffFieldError
                  error={fieldErrors.productCode}
                  identifier="product_code"
                />
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 240 }} required>
                  <InputLabel size="small">{t`Product group`}</InputLabel>
                  <Select
                    name="product_group"
                    required
                    size="small"
                    label={t`Product group`}
                    value={productDetails?.product_group}
                    error={fieldErrors.productGroup.length > 0}
                    onChange={e => handleChange(e.target.value, 'product_group')}
                  >
                    {productGroupOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <ProductTariffFieldError
                    error={fieldErrors.productGroup}
                    identifier="product_group"
                  />
                </FormControl>
                <Box sx={{ p: 1 }}>
                  <InfoTooltip
                    title={
                      <Typography variant="body2">
                        {t`Product type affects the sales rules used for the product. Available product
                  types are managed in the product parameters section.`}
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>
            {/* PRODUCT CODE AND GROUP EOF */}

            {/* TRANSLATIONS BOF */}
            <Box sx={{ my: 4 }}>
              <StyledLabel>{t`Product name and translations`}</StyledLabel>
              <Box>
                <FormControl sx={{ minWidth: 300, mr: 2, mt: 2 }} size="small" required>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontSize={18} sx={{ mr: 1, minWidth: 30 }} color="text.secondary">
                      FI
                    </Typography>
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="name"
                      label={t`Name`}
                      locale={'fi'}
                      setTranslations={handleSetTranslations}
                      size="small"
                    />
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="short_name"
                      label={t`Short name`}
                      locale={'fi'}
                      required={false}
                      setTranslations={handleSetTranslations}
                      size="small"
                      sx={{ ml: 3 }}
                    />
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="description"
                      label={t`Description`}
                      locale={'fi'}
                      required={false}
                      setTranslations={handleSetTranslations}
                      size="small"
                      sx={{ ml: 3 }}
                    />
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="additional_name"
                      label={t`Additional name`}
                      locale={'fi'}
                      required={false}
                      setTranslations={handleSetTranslations}
                      size="small"
                      sx={{ ml: 3 }}
                    />
                  </Box>
                </FormControl>
              </Box>
              <Box>
                <FormControl sx={{ minWidth: 300, mr: 2, mt: 2 }} size="small" required>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontSize={18} sx={{ mr: 1, minWidth: 30 }} color="text.secondary">
                      SV
                    </Typography>
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="name"
                      label={t`Name`}
                      locale={'sv'}
                      setTranslations={handleSetTranslations}
                      size="small"
                    />
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="short_name"
                      label={t`Short name`}
                      locale={'sv'}
                      required={false}
                      setTranslations={handleSetTranslations}
                      size="small"
                      sx={{ ml: 3 }}
                    />
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="description"
                      label={t`Description`}
                      locale={'sv'}
                      required={false}
                      setTranslations={handleSetTranslations}
                      size="small"
                      sx={{ ml: 3 }}
                    />
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="additional_name"
                      label={t`Additional name`}
                      locale={'sv'}
                      required={false}
                      setTranslations={handleSetTranslations}
                      size="small"
                      sx={{ ml: 3 }}
                    />
                  </Box>
                </FormControl>
              </Box>
              <Box>
                <FormControl sx={{ minWidth: 300, mr: 2, mt: 2 }} size="small" required>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontSize={18} sx={{ mr: 1, minWidth: 30 }} color="text.secondary">
                      EN
                    </Typography>
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="name"
                      label={t`Name`}
                      locale={'en'}
                      setTranslations={handleSetTranslations}
                      size="small"
                    />
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="short_name"
                      label={t`Short name`}
                      locale={'en'}
                      setTranslations={handleSetTranslations}
                      size="small"
                      required={false}
                      sx={{ ml: 3 }}
                    />
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="description"
                      label={t`Description`}
                      locale={'en'}
                      required={false}
                      setTranslations={handleSetTranslations}
                      size="small"
                      sx={{ ml: 3 }}
                    />
                    <TranslationInput
                      translations={productDetails?.translations ?? []}
                      field="additional_name"
                      label={t`Additional name`}
                      locale={'en'}
                      required={false}
                      setTranslations={handleSetTranslations}
                      size="small"
                      sx={{ ml: 3 }}
                    />
                  </Box>
                </FormControl>
              </Box>
            </Box>
            {/* TRANSLATIONS EOF */}

            {/* VALIDITY BOF */}
            <Box sx={{ my: 4 }}>
              <StyledLabel>{t`Product validity`}</StyledLabel>
              <Box sx={{ display: 'flex' }}>
                <FormControl sx={{ minWidth: 200, mr: 1, mt: 2 }} size="small" required>
                  <TextField
                    name="usage_validity_duration"
                    required
                    label={t`Usage validity duration`}
                    size="small"
                    type="number"
                    value={validityLength}
                    error={fieldErrors.usageValidityDuration?.length > 0}
                    onChange={e => handleValidityLength(Number.parseInt(e.target.value, 10))}
                    slotProps={{ htmlInput: { min: 0 } }}
                  />
                  <ProductTariffFieldError
                    error={fieldErrors.usageValidityDuration}
                    identifier="usage_validity_duration"
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 120, mr: 3, mt: 2 }} required>
                  <InputLabel size="small">{t`Time unit`}</InputLabel>
                  <Select
                    name="validity_format"
                    required
                    size="small"
                    label={t`Validity format`}
                    value={validityFormat ?? ''}
                    onChange={e => {
                      handleValidityFormat(e.target.value as DurationUnit)
                    }}
                  >
                    <MenuItem value={DurationUnit.DAYS}>{c('unit').t`d`}</MenuItem>
                    <MenuItem value={DurationUnit.HOURS}>{c('unit').t`h`}</MenuItem>
                    <MenuItem value={DurationUnit.MINUTES}>{c('unit').t`min`}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex' }}>
                <Box sx={{ mr: 2, mt: 2 }}>
                  <FormControl sx={{ minWidth: 240 }} required>
                    <InputLabel size="small">{t`Allowed activation method`}</InputLabel>
                    <Select
                      name="validity_begins_from"
                      required
                      multiple
                      size="small"
                      label={t`Allowed activation method`}
                      value={productDetails?.usage_trigger_event ?? []}
                      error={fieldErrors.usageTriggerEvent?.length > 0}
                      onChange={e =>
                        handleAllowedActivationMethodChange(e.target.value, 'usage_trigger_event')
                      }
                    >
                      {allowedActivationMethodOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText sx={{ mt: 0 }}>{t`You can select multiple`}</FormHelperText>
                    <ProductTariffFieldError
                      error={fieldErrors.usageTriggerEvent}
                      identifier="usage_trigger_event"
                    />
                  </FormControl>
                  <Box sx={{ p: 1, float: 'right' }}>
                    <AllowedActivationMethodsInfoTooltip />
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column">
                  {productDetails?.usage_trigger_event?.includes(
                    AllowedActivationMethods.TIMED
                  ) && (
                    <Box>
                      <FormControl sx={{ minWidth: 240, mr: 3, mt: 2 }} size="small" required>
                        <TextField
                          name="max_purchase_advance_duration"
                          required
                          label={t`Purchase in advance max`}
                          size="small"
                          type="number"
                          value={maxPurchaseAdvanceDuration}
                          error={fieldErrors.maxPurchaseAdvanceDuration?.length > 0}
                          onChange={e =>
                            handleMaxPurchaseAdvanceDuration(Number.parseInt(e.target.value, 10))
                          }
                          slotProps={{ htmlInput: { min: 0 } }}
                        />
                        <ProductTariffFieldError
                          error={fieldErrors.maxPurchaseAdvanceDuration}
                          identifier="max_purchase_advance_duration"
                        />
                      </FormControl>
                      <FormControl sx={{ minWidth: 120, mr: 3, mt: 2 }} required>
                        <InputLabel size="small">{t`Time unit`}</InputLabel>
                        <Select
                          name="max_purchase_advance_duration_format"
                          required
                          size="small"
                          label={t`Validity format`}
                          value={maxPurchaseAdvanceDurationFormat ?? ''}
                          onChange={e => {
                            handleMaxPurchaseAdvanceDurationFormat(e.target.value as DurationUnit)
                          }}
                        >
                          <MenuItem value={DurationUnit.DAYS}>{c('unit').t`d`}</MenuItem>
                          <MenuItem value={DurationUnit.HOURS}>{c('unit').t`h`}</MenuItem>
                          <MenuItem value={DurationUnit.MINUTES}>{c('unit').t`min`}</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  {productDetails?.usage_trigger_event?.includes(
                    AllowedActivationMethods.FIRST_USE
                  ) && (
                    <Box>
                      <FormControl sx={{ minWidth: 240, mr: 3, mt: 2 }} size="small" required>
                        <TextField
                          name="duration"
                          required
                          label={t`Activation window duration`}
                          size="small"
                          type="number"
                          value={activityWindowDuration}
                          error={fieldErrors.duration?.length > 0}
                          onChange={e =>
                            handleActivationWindowDuration(
                              Math.max(0, Number.parseInt(e.target.value, 10))
                            )
                          }
                        />
                        <ProductTariffFieldError
                          error={fieldErrors.duration}
                          identifier="duration"
                        />
                      </FormControl>
                      <FormControl sx={{ minWidth: 120, mr: 3, mt: 2 }} required>
                        <InputLabel size="small">{t`Time unit`}</InputLabel>
                        <Select
                          name="duration_format"
                          required
                          size="small"
                          label={t`Validity format`}
                          value={activityWindowDurationFormat ?? ''}
                          onChange={e => {
                            handleActivationWindowDurationFormat(e.target.value as DurationUnit)
                          }}
                        >
                          <MenuItem value={DurationUnit.DAYS}>{c('unit').t`d`}</MenuItem>
                          <MenuItem value={DurationUnit.HOURS}>{c('unit').t`h`}</MenuItem>
                          <MenuItem value={DurationUnit.MINUTES}>{c('unit').t`min`}</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            {/* VALIDITY EOF */}

            {/* AVAILABILITY AND USAGE BOF */}
            <Box sx={{ my: 4 }}>
              <StyledLabel>{t`Product availability and usage`}</StyledLabel>

              <Box>
                <FormControl sx={{ minWidth: 240, mt: 2 }}>
                  <InputLabel size="small">{t`Customer group`}</InputLabel>
                  <Select
                    name="customer_group"
                    size="small"
                    label={t`Customer group`}
                    value={productDetails?.customer_group ?? ''}
                    error={fieldErrors.customerGroup?.length > 0}
                    onChange={e => handleChange(e.target.value, 'customer_group')}
                  >
                    {customerGroupOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <ProductTariffFieldError
                    error={fieldErrors.customerGroup}
                    identifier="customer_group"
                  />
                </FormControl>
              </Box>
              <Box>
                <FormControl sx={{ minWidth: 240, mt: 2 }} required>
                  <InputLabel size="small">{t`Allowed tokens`}</InputLabel>
                  <Select
                    name="allowed_tokens"
                    required
                    multiple
                    size="small"
                    label={t`Allowed tokens`}
                    value={productDetails?.allowed_tokens ?? []}
                    error={fieldErrors.allowedTokens?.length > 0}
                    onChange={e => handleChange(e.target.value, 'allowed_tokens')}
                  >
                    {allowedTokenOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={{ mt: 0 }}>{t`You can select multiple`}</FormHelperText>
                  <ProductTariffFieldError
                    error={fieldErrors.allowedTokens}
                    identifier="allowed_tokens"
                  />
                </FormControl>
              </Box>
              <Box>
                <FormControl sx={{ minWidth: 240, mt: 2 }} required>
                  <InputLabel size="small">{t`Value added tax`}</InputLabel>
                  <Select
                    name="vat_code"
                    required
                    size="small"
                    label={t`Value added tax`}
                    value={productDetails?.vat_code ?? ''}
                    error={fieldErrors.vatCode?.length > 0}
                    onChange={e => handleChange(e.target.value, 'vat_code')}
                  >
                    {vatDefinitionOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <ProductTariffFieldError error={fieldErrors.vatCode} identifier="vat_code" />
                </FormControl>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <FormControl sx={{ minWidth: 240 }} required>
                    <InputLabel size="small">{t`Shared usage`}</InputLabel>
                    <Select
                      name="shared_usage"
                      required
                      size="small"
                      label={t`Shared usage`}
                      value={productDetails?.shared_usage ?? ''}
                      onChange={e => handleChange(e.target.value, 'shared_usage')}
                    >
                      {sharedUsageOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    <ProductTariffFieldError
                      error={fieldErrors.sharedUsage}
                      identifier="shared_usage"
                    />
                  </FormControl>
                  <Box sx={{ p: 1 }}>
                    <InfoTooltip
                      title={
                        <Typography variant="body2">
                          {t`The value determines whether the product can be used only by the travel account holder or also by fellow passengers.`}
                        </Typography>
                      }
                    />
                  </Box>
                </Box>
              </Box>
              {IS_WALTTI_SERVICE_ENVIRONMENT && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <FormControl sx={{ minWidth: 240 }} size="small">
                      <TextField
                        name="max_trips_day"
                        label={t`Maximum trips per day`}
                        size="small"
                        type="number"
                        value={productDetails?.max_trips_per_day ?? ''}
                        error={fieldErrors.maxTripsPerDay?.length > 0}
                        onChange={e =>
                          handleChange(
                            e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                            'max_trips_per_day'
                          )
                        }
                      />
                      <ProductTariffFieldError
                        error={fieldErrors.maxTripsPerDay}
                        identifier="max_trips_per_day"
                      />
                    </FormControl>
                    <Box sx={{ p: 1 }}>
                      <MaximumTripsPerDayInfoTooltip />
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <FormControl sx={{ minWidth: 240 }} size="small">
                        <TextField
                          name="max_device_transfers"
                          label={t`Maximum device transfers`}
                          size="small"
                          type="number"
                          value={productDetails?.max_device_transfers ?? ''}
                          error={fieldErrors.maxDeviceTransfers?.length > 0}
                          onChange={e =>
                            handleChange(
                              e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                              'max_device_transfers'
                            )
                          }
                        />
                        <ProductTariffFieldError
                          error={fieldErrors.maxDeviceTransfers}
                          identifier="max_device_transfers"
                        />
                      </FormControl>
                      <Box sx={{ p: 1 }}>
                        <MaximumDeviceTransfersTooltip />
                      </Box>
                    </Box>
                  </Box>
                  <FormGroup sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={productDetails.print_zero_price}
                          onChange={e => handleChange(e.target.checked, 'print_zero_price')}
                          name="print_zero_price"
                          color="primary"
                        />
                      }
                      label={t`Print zero price`}
                    />
                    <ProductTariffFieldError
                      error={fieldErrors.printZeroPrice}
                      identifier="print_zero_price"
                    />
                  </FormGroup>
                </Box>
              )}
              <Box>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={productDetails.strong_identification_required}
                        onChange={e =>
                          handleChange(e.target.checked, 'strong_identification_required')
                        }
                        name="strong_identification_required"
                        color="primary"
                      />
                    }
                    label={t`Strong identification required`}
                  />
                  <ProductTariffFieldError
                    error={fieldErrors.strongIdentificationRequired}
                    identifier="strong_identification_required"
                  />
                </FormGroup>
              </Box>
            </Box>
            {/* AVAILABILITY AND USAGE EOF */}
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save product`}
            </CustomLoadingButton>
          </Box>
        </Grid>
      </Grid>
    </WithLoader>
  )
}
